import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import Button from '../../components/ui/Button';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

const ManageHouseView = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [houseView, setHouseView] = useState({
    conservative: [],
    moderate_conservative: [],
    moderate: [],
    moderate_aggressive: [],
    aggressive: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savingFund, setSavingFund] = useState(null); // {profileKey, index}
  const [errorMessage, setErrorMessage] = useState('');

  const profiles = [
    { key: 'conservative', label: 'Conservative', color: '#3B82F6', desc: 'Capital Preservation Strategy' },
    { key: 'moderate_conservative', label: 'Moderate Conservative', color: '#8B5CF6', desc: 'Stable Income Strategy' },
    { key: 'moderate', label: 'Moderate', color: '#10B981', desc: 'Balanced Growth Strategy' },
    { key: 'moderate_aggressive', label: 'Moderate Aggressive', color: '#F59E0B', desc: 'Growth Strategy' },
    { key: 'aggressive', label: 'Aggressive', color: '#EF4444', desc: 'Aggressive Growth Strategy' }
  ];

  useEffect(() => {
    fetchHouseView();
  }, []);

  const fetchHouseView = async () => {
    try {
      setLoading(true);
      const response = await api.get('/house-view');
      setHouseView(response.data);
    } catch (error) {
      console.error('Failed to fetch house view:', error);
      setErrorMessage('Failed to load house view.');
    } finally {
      setLoading(false);
    }
  };

  const addFundToProfile = (profileKey) => {
    setHouseView(prev => {
      const current = prev[profileKey] || [];
      if (current.length >= 5) return prev;
      return { ...prev, [profileKey]: [...current, { name: '', percentage: 0 }] };
    });
  };

  const updateFund = (profileKey, index, field, value) => {
    setHouseView(prev => {
      const updated = [...(prev[profileKey] || [])];
      if (field === 'percentage') {
        const numVal = value === '' ? 0 : parseFloat(value);
        if (isNaN(numVal) || numVal < 0 || numVal > 100) return prev;
        updated[index] = { ...updated[index], percentage: numVal };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return { ...prev, [profileKey]: updated };
    });
  };

  const removeFund = async (profileKey, index) => {
    const fund = houseView[profileKey][index];
    if (fund.id) {
      try {
        await api.delete(`/house-view/${fund.id}`);
      } catch (error) {
        console.error('Failed to delete fund:', error);
        setErrorMessage('Failed to delete fund.');
        return;
      }
    }
    setHouseView(prev => {
      const updated = (prev[profileKey] || []).filter((_, i) => i !== index);
      return { ...prev, [profileKey]: updated };
    });
  };

  const getProfileTotal = (profileKey) => {
    return (houseView[profileKey] || []).reduce((sum, f) => sum + Number(f.percentage || 0), 0);
  };

  const saveFund = async (profileKey, index) => {
    const fund = houseView[profileKey][index];
    if (!fund.name || fund.name.trim() === '') return;

    setSavingFund({ profileKey, index });
    try {
      if (fund.id) {
        // Update existing
        await api.put(`/house-view/${fund.id}`, {
          fund_name: fund.name,
          fund_percentage: fund.percentage,
          display_order: index
        });
      } else {
        // Create new
        const response = await api.post('/house-view', {
          profile_key: profileKey,
          fund_name: fund.name,
          fund_percentage: fund.percentage,
          display_order: index
        });
        // Update local state with the returned ID
        setHouseView(prev => {
          const updated = [...(prev[profileKey] || [])];
          updated[index] = { ...updated[index], id: response.data.id };
          return { ...prev, [profileKey]: updated };
        });
      }
    } catch (error) {
      console.error('Failed to save fund:', error);
      setErrorMessage('Failed to save fund.');
    } finally {
      setSavingFund(null);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      // Save all funds that have been added but not yet saved (no ID)
      for (const profileKey of profiles.map(p => p.key)) {
        const funds = houseView[profileKey] || [];
        for (let i = 0; i < funds.length; i++) {
          const fund = funds[i];
          if (fund.name && fund.name.trim() !== '') {
            if (!fund.id) {
              const response = await api.post('/house-view', {
                profile_key: profileKey,
                fund_name: fund.name,
                fund_percentage: fund.percentage,
                display_order: i
              });
              // Update local state with the returned ID
              setHouseView(prev => {
                const updated = [...(prev[profileKey] || [])];
                updated[i] = { ...updated[i], id: response.data.id };
                return { ...prev, [profileKey]: updated };
              });
            } else {
              await api.put(`/house-view/${fund.id}`, {
                fund_name: fund.name,
                fund_percentage: fund.percentage,
                display_order: i
              });
            }
          }
        }
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save house view:', error);
      setErrorMessage('Failed to save house view.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.surface }}>
        <Loader2 className="w-16 h-16 mx-auto animate-spin" style={{ color: theme.primary }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <header className="border-b" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.text }}>Manage House View</h1>
              <p className="text-sm" style={{ color: theme.textSecondary }}>Configure global fund allocations across risk profiles</p>
            </div>
            <Button variant="ghost" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin Panel
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {errorMessage && <p className="text-sm text-red-600 dark:text-red-400 mb-4">{errorMessage}</p>}

        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-sm" style={{ color: theme.textSecondary }}>
              Add funds to each risk profile. Up to 5 funds per profile. These will feed into quotations when pulling from House View.
            </p>
          </div>
          <Button
            onClick={handleSaveAll}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saved ? 'Saved ✓' : 'Save All Changes'}
          </Button>
        </div>

        {profiles.map(({ key, label, color, desc }) => {
          const funds = houseView[key] || [];
          const profileTotal = getProfileTotal(key);
          const isProfileValid = funds.length === 0 || Math.abs(profileTotal - 100) < 0.01;

          return (
            <div key={key} className="rounded-xl overflow-hidden" style={{ border: `1px solid ${theme.border}` }}>
              {/* Profile header */}
              <div className="px-5 py-3 flex items-center gap-3" style={{ backgroundColor: theme.card }}>
                <div className="w-3 h-8 rounded-full" style={{ backgroundColor: color }} />
                <div className="flex-1">
                  <p className="text-sm font-semibold" style={{ color: theme.text }}>{label}</p>
                  <p className="text-xs" style={{ color: theme.textSecondary }}>{desc}</p>
                </div>
                {funds.length > 0 && (
                  <div className={`text-xs font-bold px-2.5 py-1 rounded-full ${isProfileValid ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`}>
                    {profileTotal.toFixed(0)}%
                  </div>
                )}
              </div>

              {/* Fund list */}
              <div className="px-5 py-3 space-y-2" style={{ backgroundColor: theme.surface }}>
                {funds.map((fund, idx) => {
                  const isSavingThisFund = savingFund?.profileKey === key && savingFund?.index === idx;
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                      <input
                        type="text"
                        placeholder="Fund name..."
                        value={fund.name}
                        onChange={(e) => updateFund(key, idx, 'name', e.target.value)}
                        onBlur={() => fund.name && fund.name.trim() !== '' && saveFund(key, idx)}
                        className="flex-1 text-sm px-3 py-2 rounded-lg"
                        style={{
                          backgroundColor: theme.card,
                          border: `1px solid ${theme.border}`,
                          color: theme.text
                        }}
                      />
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={fund.percentage || ''}
                          onChange={(e) => updateFund(key, idx, 'percentage', e.target.value)}
                          onBlur={() => fund.name && fund.name.trim() !== '' && saveFund(key, idx)}
                          placeholder="0"
                          className="w-16 text-center text-sm font-medium px-2 py-2 rounded-lg"
                          style={{
                            backgroundColor: theme.card,
                            border: `1px solid ${theme.border}`,
                            color: theme.text
                          }}
                        />
                        <span className="text-xs" style={{ color: theme.textSecondary }}>%</span>
                      </div>
                      {isSavingThisFund && <Loader2 className="w-4 h-4 animate-spin" style={{ color: theme.primary }} />}
                      <button
                        onClick={() => removeFund(key, idx)}
                        className="text-xs px-2 py-1.5 rounded-md transition-colors"
                        style={{ color: theme.textSecondary, backgroundColor: 'transparent' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(239,68,68,0.1)'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}

                {/* Add fund button */}
                {funds.length < 5 && (
                  <button
                    onClick={() => addFundToProfile(key)}
                    className="flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors w-full"
                    style={{
                      color: color,
                      backgroundColor: 'transparent',
                      border: `1px dashed ${color}40`
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${color}10`}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span className="text-lg leading-none" style={{ color }}>+</span>
                    Add fund {funds.length > 0 && `(${5 - funds.length} remaining)`}
                  </button>
                )}

                {funds.length === 5 && (
                  <p className="text-xs text-center py-1" style={{ color: theme.textMuted }}>Maximum 5 funds reached</p>
                )}
              </div>
            </div>
          );
        })}
      </main>
    </div>
  );
};

export default ManageHouseView;
